import React, { useState } from 'react';
import {
  AtSign, Building2, ChevronDown, ChevronUp, CreditCard, Globe, Hash,
  Mail, MapPin, MessageSquare, Phone,
} from 'lucide-react';
import { ClickToCallButton } from '../shared/ClickToCallButton';
import { regionDisplayName } from '../../lib/utils/italianRegions';

interface PartnerAgnosticInfoRowsProps {
  /** View-model Client-like del partner (mapPartnerToClientView). */
  selectedClient: any;
  title: string;
  handleCopy: (value: string, label: string) => void;
}

// Stesso PropertyRow storico di LeadDesktopLayout: il ramo partner mantiene
// l'aspetto pre-redesign (design partner congelato il 12/08, non si tocca).
const PropertyRow = ({
  icon: Icon, label, value, onClick, isLink = false, children,
}: {
  icon: any; label: string; value?: string; onClick?: () => void; isLink?: boolean; children?: React.ReactNode;
}) => (
  <div
    className={`flex items-center justify-between py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-center space-x-2.5">
      <Icon className="h-4 w-4 text-[#787774] dark:text-gray-500 flex-shrink-0" />
      <span className="text-sm text-[#787774] dark:text-gray-400">{label}</span>
    </div>
    {children || (
      <span className={`text-sm ${isLink ? 'text-[#337EA9] dark:text-[#529CCA] hover:underline' : 'text-[#37352F] dark:text-white'} truncate max-w-[220px]`}>
        {value}
      </span>
    )}
  </div>
);

/**
 * Righe informative agnostiche della colonna sinistra per il ramo PARTNER
 * (email/telefono/WhatsApp/indirizzo + campi espandibili dal view-model):
 * markup identico al pannello pre-redesign, in coerenza con PartnerInfoExtras.
 */
export function PartnerAgnosticInfoRows({ selectedClient, title, handleCopy }: PartnerAgnosticInfoRowsProps) {
  const [expanded, setExpanded] = useState(false);

  const addressParts = [
    selectedClient.address || selectedClient.street_address,
    selectedClient.postal_code,
    selectedClient.city,
    selectedClient.province ? `(${selectedClient.province})` : null,
    regionDisplayName(selectedClient.region),
    selectedClient.country,
  ].filter(Boolean);
  const fullAddress = addressParts.join(', ');

  return (
    <>
      <div className="flex items-center justify-between py-3">
        <h3 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wide">
          {title}
        </h3>
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1.5 text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 rounded transition-colors"
          type="button"
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      <div className="space-y-0.5">
        {selectedClient.email && (
          <PropertyRow icon={Mail} label="Email" value={selectedClient.email} isLink
            onClick={() => handleCopy(selectedClient.email, 'Email')} />
        )}
        {selectedClient.phone && (
          <PropertyRow icon={Phone} label="Telefono" onClick={() => handleCopy(selectedClient.phone, 'Telefono')}>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-[#37352F] dark:text-white truncate max-w-[180px]">{selectedClient.phone}</span>
              <ClickToCallButton phoneNumber={selectedClient.phone} size="sm" />
            </div>
          </PropertyRow>
        )}
        {(selectedClient.whatsapp_number || selectedClient.mobile) && (
          <PropertyRow icon={MessageSquare} label="WhatsApp"
            onClick={() => handleCopy(selectedClient.whatsapp_number || selectedClient.mobile, 'WhatsApp')}>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-[#37352F] dark:text-white truncate max-w-[180px]">
                {selectedClient.whatsapp_number || selectedClient.mobile}
              </span>
              <ClickToCallButton phoneNumber={selectedClient.whatsapp_number || selectedClient.mobile || ''} size="sm" />
            </div>
          </PropertyRow>
        )}
        {addressParts.length > 0 && (
          <PropertyRow icon={MapPin} label="Indirizzo" value={fullAddress}
            onClick={() => handleCopy(fullAddress, 'Indirizzo')} />
        )}

        {expanded && (
          <>
            {selectedClient.website && (
              <PropertyRow icon={Globe} label="Sito web" value={selectedClient.website} isLink
                onClick={() => handleCopy(selectedClient.website, 'Sito web')} />
            )}
            {selectedClient.pec && (
              <PropertyRow icon={AtSign} label="PEC" value={selectedClient.pec} isLink
                onClick={() => handleCopy(selectedClient.pec, 'PEC')} />
            )}
            {selectedClient.client_code && (
              <PropertyRow icon={Hash} label="Codice" value={selectedClient.client_code}
                onClick={() => handleCopy(selectedClient.client_code, 'Codice')} />
            )}
            {selectedClient.vat_number && (
              <PropertyRow icon={CreditCard} label="P.IVA" value={selectedClient.vat_number}
                onClick={() => handleCopy(selectedClient.vat_number, 'P.IVA')} />
            )}
            {selectedClient.trade_name && (
              <PropertyRow icon={Building2} label="Insegna" value={selectedClient.trade_name} />
            )}
          </>
        )}
      </div>
    </>
  );
}
